import { supabase } from '@/lib/supabase';

export type LogLevel = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
export type LogResult = 'success' | 'failure' | 'warning' | 'unknown';

export interface LogPayload {
  /** Acción concreta, ej. "product_created", "login_failed". */
  action: string;
  /** Módulo del sistema, ej. "auth", "products", "pos", "cash". */
  module?: string;
  /** Nivel de severidad. */
  level?: LogLevel;
  /** Resultado de la operación. */
  result?: LogResult;
  /** Descripción legible para el administrador. */
  description?: string;
  /** Tipo del registro afectado, ej. "product", "invoice". */
  targetType?: string;
  /** Identificador del registro afectado. */
  targetId?: string;
  /** Datos adicionales (nunca contraseñas ni tokens). */
  metadata?: Record<string, unknown>;
  /** Sucursal asociada al evento. */
  branchId?: string | null;
}

/**
 * Registra un evento de actividad en el sistema centralizado de auditoría.
 * Nunca lanza ni bloquea la operación del usuario: si falla, se ignora.
 */
export async function logActivity(payload: LogPayload): Promise<void> {
  try {
    await supabase.rpc('log_activity', {
      p_action: payload.action,
      p_module: payload.module ?? 'general',
      p_level: payload.level ?? 'INFO',
      p_result: payload.result ?? 'success',
      p_description: payload.description ?? null,
      p_target_type: payload.targetType ?? null,
      p_target_id: payload.targetId ?? null,
      p_metadata: payload.metadata ?? null,
      p_branch_id: payload.branchId ?? null,
    });
  } catch {
    // El registro de auditoría nunca debe romper la acción del usuario.
  }
}

/** Registra un error de la aplicación (nivel ERROR por defecto). */
export function logError(
  module: string,
  action: string,
  description: string,
  metadata?: Record<string, unknown>
): void {
  void logActivity({
    module,
    action,
    level: 'ERROR',
    result: 'failure',
    description,
    metadata,
  });
}

/** Registra un error crítico de la aplicación. */
export function logCritical(
  module: string,
  action: string,
  description: string,
  metadata?: Record<string, unknown>
): void {
  void logActivity({
    module,
    action,
    level: 'CRITICAL',
    result: 'failure',
    description,
    metadata,
  });
}